import { useEffect, useState } from 'react';
import { cargarDemo } from '../lib/demo.js';
import { reiniciarTodo } from '../lib/acceso.js';
import { enPractica } from '../lib/practica.js';
import { esCopiaDeTienda } from '../lib/copia-de-tienda.js';
import { api } from '../sync/client.js';
import { useT } from '../i18n/index.js';
import { Ico } from './Iconos.js';
import { Modal } from './Modal.js';

/**
 * ¿Es este un servidor de prueba?
 *
 * Two signals, and only one of them is load-bearing. `import.meta.env.DEV` is a convenience so
 * `pnpm dev` works with no server at all. The one that actually protects a shop is
 * `instalacionAbierta`: the server saying it is not production and has no SETUP_TOKEN. A client
 * cannot fake that, and `cargarDemo` checks it again before writing anything.
 *
 * Why it needs protecting at all: the log is append-only. Fifty demo events landing in a real
 * shop are not a screen that looks wrong, they are twenty-four invented products, sales in the
 * ledger and staff on the payroll that can only be removed by exporting the log, filtering it
 * and importing it again. There is no undo, so there are two locks.
 */
function useModoPrueba(): boolean {
  const [abierta, setAbierta] = useState(false);
  useEffect(() => {
    // The practice shop has no server to ask, and asks nothing.
    if (enPractica()) return;
    let vivo = true;
    void api
      .health()
      .then((h) => vivo && setAbierta(Boolean(h.instalacionAbierta)))
      .catch(() => {});
    return () => {
      vivo = false;
    };
  }, []);
  return import.meta.env.DEV || abierta;
}

/**
 * El botón que se salta toda la configuración.
 *
 * Setting a shop up by hand is the right flow for somebody who owns one and pure friction for
 * somebody trying to find out whether the thing works. One tap builds a whole pulpería out of
 * real events, so what appears on screen afterwards was reduced and projected the same way a
 * real day would be.
 *
 * Failures are shown rather than swallowed. If a reducer refuses something the demo emits, that
 * is a genuine finding and the worst possible response is a spinner that quietly stops.
 */
export function DatosDePrueba() {
  const t = useT();
  const modoPrueba = useModoPrueba();
  const [paso, setPaso] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [borrando, setBorrando] = useState(false);

  // Nor in a copy of the real shop: loading the demo into it, or wiping it, is never the point.
  if (!modoPrueba || esCopiaDeTienda()) return null;

  async function cargar() {
    setError(null);
    setAviso(null);
    setPaso('…');
    try {
      const r = await cargarDemo(setPaso);
      if (r === 'yaHabia') setAviso(t('prueba.yaHay'));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setPaso(null);
    }
  }

  /**
   * Back to nothing, on both sides.
   *
   * The first version of this wiped only the local copy, which left the device with no
   * credentials facing a server that still considered itself installed — and the install
   * screen, correctly, refused. A reset that bricks the app is not a reset.
   */
  async function reiniciar(conDatos: boolean) {
    setError(null);
    try {
      await reiniciarTodo(conDatos);
    } catch (e) {
      setBorrando(false);
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <section className="rounded-2xl border border-dashed border-slate-700 p-4">
      <button
        type="button"
        className="btn-secundario w-full"
        disabled={paso !== null}
        onClick={() => void cargar()}
      >
        <Ico icono="semilla" />
        {paso === null ? t('prueba.cargar') : t('prueba.creando', { paso })}
      </button>
      <p className="mt-3 text-xs text-slate-500">{t('prueba.explicacion')}</p>
      {aviso && <p className="mt-2 text-xs text-amber-300">{aviso}</p>}
      {error && <p className="mt-2 text-xs text-rose-400">{t('prueba.fallo', { error })}</p>}

      <button
        type="button"
        className="btn-texto mt-4 gap-1 text-xs text-slate-500"
        onClick={() => setBorrando(true)}
      >
        <Ico icono="quitar" className="size-3.5" />
        {t('prueba.borrar')}
      </button>

      {borrando && (
        <Modal
          titulo={t('prueba.borrar')}
          onCerrar={() => setBorrando(false)}
          pie={
            <div className="flex gap-3">
              <button
                type="button"
                className="btn-secundario flex-1"
                onClick={() => void reiniciar(false)}
              >
                <Ico icono="vacio" />
                {t('prueba.vacio')}
              </button>
              <button
                type="button"
                className="btn-primario flex-1"
                onClick={() => void reiniciar(true)}
              >
                <Ico icono="semilla" />
                {t('prueba.conDatos')}
              </button>
            </div>
          }
        >
          <p className="text-sm text-slate-300">{t('prueba.confirmarBorrar')}</p>
          <p className="mt-2 text-sm text-slate-400">{t('prueba.borrarAviso')}</p>
        </Modal>
      )}
    </section>
  );
}
